<script setup lang="ts">
import type { ModernSelectProps } from "../AutoComplete/VModernSelectProps.interface";
import type { LocaleGroup } from "./locale";
import { computed } from "vue";
import VModernSelect from "../AutoComplete/VModernSelect.vue";
import VIcon from "../Icon/VIcon.vue";
import { countryNames, englishCountryName, englishLanguageName, englishSecondaryName, FLAG_IMAGE_CLASS, languageFlagSrc, languageNames } from "./locale";
import VLocaleOption from "./VLocaleOption.vue";

interface Props {
  availableLocales?: string[]
  /**
   * Shows the locales in these groups, in this order, under a header per group. A locale of `availableLocales` that
   * no group names is left out, so give the last group the rest.
   */
  localeGroups?: LocaleGroup[]
  showFlag?: boolean
  updateLocale?: boolean
  countries?: boolean
  /** Shows the locale code instead of the name, in the options and in the closed field (flag + "NL"). */
  short?: boolean
}

const props = withDefaults(defineProps<Props & ModernSelectProps>(), {
  availableLocales: () => [],
  localeGroups: undefined,
  showFlag: true,
  updateLocale: false,
  countries: false,
  short: false,
  rootClass: "",
  modelValue: null,
  iconLeft: undefined,
  iconRight: "chevron-down",
  loading: false,
  autoSize: false,
  disabled: false,
  errorMessage: null,
  hideClear: true,
  clearSearchOnSelect: false,
  clearValueOnSelect: false,
  singleItemAutoSelect: false,
  hasPerms: true,
  keyFieldName: null,
  uniqueKeyOfObject: "id",
  searchFieldName: "name" as Extract<keyof any, string>,
  labelLeft: "",
  required: false,
  debug: false,
  placeholder: null,
  items: () => [],
  excludedItems: () => [],
  loadParams: () => { },
  initialItemProvider: async () => null,
  variant: "is-gray",
  showGroup: undefined,
  watchForChange: null,
  size: "medium",
  groupFieldName: null,
  placement: "bottom-start",
});

const emits = defineEmits(["changeLocale"]);

const locale = defineModel<string>({ required: true });

function localeItem(locale: string, group: string | null) {
  return {
    id: locale,
    value: locale,
    name: props.countries
      ? countryNames[locale as keyof typeof countryNames] ?? locale
      : languageNames[locale as keyof typeof languageNames] ?? locale,
    // `englishName` is only searched: the options show it as the grey line above the name (see VLocaleOption).
    englishName: (props.countries ? englishCountryName(locale) : englishLanguageName(locale)) ?? "",
    group,
  };
}

const localeItems = computed(() => {
  if (!props.localeGroups) {
    return props.availableLocales.map(locale => localeItem(locale, null));
  }
  const available = new Set(props.availableLocales);
  return props.localeGroups.flatMap(group => group.locales
    .filter(locale => available.has(locale))
    .map(locale => localeItem(locale, group.label)));
});

function flagSrc(value: string): string {
  return props.countries ? `/flags/${value.toUpperCase()}.svg` : languageFlagSrc(value);
}

// The closed short select shows the code the way a language pill reads it: "NL", "EN".
function shortCode(value: string): string {
  return value.toUpperCase();
}

function onLocaleChange(value: string) {
  if (props.updateLocale) {
    emits("changeLocale", value);
  }
}
</script>

<template>
  <VModernSelect
    v-bind="$props"
    v-model="locale"
    :class="$attrs.class"
    :items="localeItems"
    :group-field-name="localeGroups ? 'group' : groupFieldName"
    key-field-name="value"
    search-field-name="name"
    :search-also-field-names="['englishName']"
    auto-size
    :trigger-icon-tile="!showFlag"
    @update:model-value="onLocaleChange"
  >
    <template #iconLeft="{ item }">
      <img
        v-if="showFlag && item"
        :class="FLAG_IMAGE_CLASS"
        :src="flagSrc(item.value)"
        alt=""
      >
      <VIcon v-else name="globe" />
    </template>
    <template v-if="short" #trigger-value="{ item, placeholder }">
      <span v-if="item" class="truncate font-normal tracking-[0.05em]" data-test="locale-select-short-value">{{ shortCode(item.value) }}</span>
      <span v-else class="truncate text-(--input-placeholder)">{{ placeholder }}</span>
    </template>
    <template #item="{ item, label, highlight }">
      <VLocaleOption
        :locale="item.value"
        :name="short ? item.value : item.name"
        :flag-src="showFlag ? flagSrc(item.value) : null"
        :secondary="englishSecondaryName(item.value, item.name, countries)"
        :label="short ? undefined : label"
        :highlight="highlight"
      />
    </template>
  </VModernSelect>
</template>
